// frontend/src/landing/pages/LoginPage.js
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { useTheme } from '../../context/ThemeContext';
import { C } from '../../styles/theme';
import host from '../../host';

const LoginPage = () => {
    const navigate = useNavigate();
    const { login, setSelectedPortal } = useAuth();
    const { t, language, toggleLanguage } = useLanguage();
    const { theme, toggleTheme } = useTheme();
    const [langDropdownOpen, setLangDropdownOpen] = useState(false);
    const langDropdownRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (langDropdownRef.current && !langDropdownRef.current.contains(e.target)) {
                setLangDropdownOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Not Electron-specific despite the old name: true on any physical POS
    // device (Capacitor POS build), false on plain browser/manager access.
    const isPosDevice = !!host.isPosDevice;

    // Responsive detection
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const [selectedPortal, setPortalLocal] = useState('pos');
    const [accountType, setAccountType] = useState('tenant');
    const [idValue, setIdValue] = useState('');
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    const handlePortalSelect = (portal) => {
        setPortalLocal(portal);
        setError('');
        setIdValue('');
        setUsername('');
        setPassword('');
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        if (!idValue || !username || !password) {
            setError(t('all_fields_required'));
            return;
        }
        setLoading(true);
        try {
            await login(username, password, idValue);
            if (isPosDevice) {
                setSelectedPortal('pos');
                navigate('/functions');
            } else {
                setSelectedPortal(selectedPortal);
                if (selectedPortal === 'manager') {
                    navigate('/portal');
                } else {
                    navigate('/functions');
                }
            }
        } catch (err) {
            setError(err.response?.data?.detail || t('invalid_credentials'));
        } finally {
            setLoading(false);
        }
    };

    const isManager = !isPosDevice && selectedPortal === 'manager';

    const posCardStyle = {
        border: selectedPortal === 'pos' ? `2px solid ${C.green}` : `1.5px solid ${C.border}`,
        background: selectedPortal === 'pos' ? C.greenLight : C.white,
        borderRadius: 12, padding: '14px 16px', cursor: 'pointer', marginBottom: 12,
        transition: 'all 0.2s ease',
    };
    const managerCardStyle = {
        border: selectedPortal === 'manager' ? `2px solid ${C.green}` : `1.5px solid ${C.border}`,
        background: selectedPortal === 'manager' ? C.greenLight : C.white,
        borderRadius: 12, padding: '14px 16px', cursor: 'pointer', transition: 'all 0.2s ease',
    };

    return (
        <div style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", display: 'flex', flexDirection: isMobile ? 'column' : 'row', height: '100vh', overflow: 'hidden' }}>
            <link rel="preconnect" href="https://fonts.googleapis.com" />
            <link href="https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap" rel="stylesheet" />

            {/* Left Panel — portal selector (hidden on POS devices and mobile) */}
            {!isPosDevice && !isMobile && <div style={{
                width: '42%', background: 'linear-gradient(160deg, var(--c-greenLight), var(--c-bg))',
                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                padding: '48px 40px', color: C.text, flexShrink: 0,
            }}>
                <div style={{ marginBottom: 40, textAlign: 'center' }}>
                    <img src={theme === 'dark' ? '/aqana-logo-light.png?v=6' : '/aqana-logo.png?v=6'} alt="Aqana" style={{ height: 34, width: 'auto', marginBottom: 4 }} />
                    <div style={{ fontSize: 13, color: C.muted, fontWeight: 500 }}>{t('restaurant_management_platform')}</div>
                </div>

                <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1.5, color: C.mutedLight, textTransform: 'uppercase',
                    marginBottom: 16, alignSelf: 'flex-start' }}>
                    {t('select_portal')}
                </div>

                <div style={{ width: '100%' }}>
                    <div style={posCardStyle} onClick={() => handlePortalSelect('pos')}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                            <div style={{ width: 36, height: 36, background: C.green, borderRadius: 8, display: 'flex',
                                alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2"
                                    strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="3" width="20" height="14" rx="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/></svg>
                            </div>
                            <div>
                                <div style={{ fontSize: 13, fontWeight: 700, color: C.text }}>{t('pos_functions')}</div>
                                <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>{t('cashier_waiter_kds')}</div>
                            </div>
                        </div>
                    </div>

                    <div style={managerCardStyle} onClick={() => handlePortalSelect('manager')}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                            <div style={{ width: 36, height: 36, background: C.green, borderRadius: 8, display: 'flex',
                                alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2"
                                    strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/></svg>
                            </div>
                            <div>
                                <div style={{ fontSize: 13, fontWeight: 700, color: C.text }}>{t('manager_portal')}</div>
                                <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>{t('admin_dashboard_desc')}</div>
                            </div>
                        </div>
                    </div>
                </div>

                <div style={{ marginTop: 'auto', textAlign: 'center', fontSize: 11, color: C.mutedLight }}>
                    <a href="#" style={{ color: C.muted, textDecoration: 'none', marginRight: 16 }}>{t('help')}</a>
                    <a href="#" style={{ color: C.muted, textDecoration: 'none' }}>{t('contact_support')}</a>
                </div>
            </div>}

            {/* Mobile brand banner */}
            {!isPosDevice && isMobile && (
                <div style={{
                    background: 'linear-gradient(160deg, var(--c-greenLight), var(--c-bg))',
                    padding: '16px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    flexShrink: 0,
                }}>
                    <img src={theme === 'dark' ? '/aqana-logo-light.png?v=6' : '/aqana-logo.png?v=6'} alt="Aqana" style={{ height: 26, width: 'auto' }} />
                    <div style={{ display: 'flex', gap: 8 }}>
                        {['pos', 'manager'].map(p => (
                            <button key={p} onClick={() => handlePortalSelect(p)} style={{
                                padding: '6px 14px', borderRadius: 8, border: selectedPortal === p ? `1.5px solid ${C.green}` : `1px solid ${C.border}`,
                                background: selectedPortal === p ? C.greenLight : C.white,
                                color: C.text, fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
                                minHeight: 36,
                            }}>
                                {p === 'pos' ? t('pos') : t('manager_portal')}
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {/* Right Panel — login form */}
            <div style={{
                flex: 1, background: C.bg, display: 'flex', flexDirection: 'column',
                alignItems: 'center', justifyContent: isMobile ? 'flex-start' : 'center',
                padding: isMobile ? '24px 20px' : '48px 56px', overflow: 'auto',
                position: 'relative',
            }}>
                {/* Theme toggle */}
                <button
                    onClick={toggleTheme}
                    title={theme === 'dark' ? t('light_mode') : t('dark_mode')}
                    aria-label="Toggle theme"
                    style={{
                        position: 'absolute', top: 16, right: 64,
                        background: 'none', border: 'none', cursor: 'pointer',
                        padding: 6, borderRadius: 6, display: 'flex', alignItems: 'center',
                        color: C.muted,
                    }}
                >
                    {theme === 'dark'
                        ? <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="5"/><line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/><line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/><line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/><line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/></svg>
                        : <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/></svg>}
                </button>
                {/* Language globe */}
                <div ref={langDropdownRef} style={{ position: 'absolute', top: 16, right: 24 }}>
                    <button
                        onClick={() => setLangDropdownOpen(!langDropdownOpen)}
                        style={{
                            background: 'none', border: 'none', cursor: 'pointer',
                            padding: 6, borderRadius: 6, display: 'flex',
                            alignItems: 'center', color: C.muted,
                        }}
                        title={t('language')}
                    >
                        <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="12" cy="12" r="10"/>
                            <path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
                        </svg>
                    </button>
                    {langDropdownOpen && (
                        <div style={{
                            position: 'absolute', top: '100%', right: 0, marginTop: 4,
                            background: C.white, border: `1px solid ${C.border}`,
                            borderRadius: 8, boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                            minWidth: 140, zIndex: 999, overflow: 'hidden',
                        }}>
                            <button
                                onClick={() => { if (language !== 'en') toggleLanguage(); setLangDropdownOpen(false); }}
                                style={{
                                    width: '100%', padding: '8px 14px', border: 'none', cursor: 'pointer',
                                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                    fontSize: 13, fontWeight: 500, fontFamily: "'Plus Jakarta Sans', sans-serif",
                                    background: language === 'en' ? C.greenLight : C.white,
                                    color: language === 'en' ? C.green : C.text,
                                }}
                            >
                                English {language === 'en' && <span style={{ color: C.green }}>&#10003;</span>}
                            </button>
                            <button
                                onClick={() => { if (language !== 'ar') toggleLanguage(); setLangDropdownOpen(false); }}
                                style={{
                                    width: '100%', padding: '8px 14px', border: 'none', cursor: 'pointer',
                                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                    fontSize: 13, fontWeight: 500, fontFamily: "'Plus Jakarta Sans', sans-serif",
                                    background: language === 'ar' ? C.greenLight : C.white,
                                    color: language === 'ar' ? C.green : C.text,
                                }}
                            >
                                العربية {language === 'ar' && <span style={{ color: C.green }}>&#10003;</span>}
                            </button>
                        </div>
                    )}
                </div>

                <div style={{ width: '100%', maxWidth: 420 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1.5, color: C.green, textTransform: 'uppercase', marginBottom: 8 }}>
                        {isManager ? t('manager_portal') : t('point_of_sale')}
                    </div>
                    <h1 style={{ fontSize: 28, fontWeight: 800, color: C.text, margin: '0 0 8px', fontFamily: 'inherit' }}>
                        {isManager ? t('access_manager_portal') : t('sign_in_to_pos')}
                    </h1>
                    <p style={{ fontSize: 14, color: C.muted, margin: '0 0 32px' }}>
                        {isManager
                            ? t('tenant_admin_access_desc')
                            : t('employee_pin_prompt_desc')}
                    </p>

                    <form onSubmit={handleSubmit}>
                        {isManager && (
                            <div style={{ display: 'flex', background: C.border, borderRadius: 10, padding: 3, marginBottom: 20, gap: 3 }}>
                                {['tenant', 'company'].map(type => (
                                    <button key={type} type="button"
                                        onClick={() => { setAccountType(type); setIdValue(''); }}
                                        style={{
                                            flex: 1, padding: '8px 0', borderRadius: 8, border: 'none', cursor: 'pointer',
                                            fontFamily: 'inherit', fontSize: 13, fontWeight: 600,
                                            background: accountType === type ? C.white : 'transparent',
                                            color: accountType === type ? C.green : C.muted,
                                            boxShadow: accountType === type ? '0 1px 4px rgba(0,0,0,0.1)' : 'none',
                                            transition: 'all 0.15s ease',
                                        }}>
                                        {type === 'tenant' ? t('tenant_account') : t('company_account')}
                                    </button>
                                ))}
                            </div>
                        )}

                        <div style={{ marginBottom: 16 }}>
                            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: C.text, marginBottom: 6 }}>
                                {isManager && accountType === 'company' ? t('company_id') : t('tenant_id')} <span style={{ color: C.red }}>*</span>
                            </label>
                            <input type="text" inputMode="numeric" maxLength={6} placeholder={t('six_digit_id')}
                                value={idValue} onChange={e => setIdValue(e.target.value.replace(/\D/g, '').slice(0, 6))} required
                                style={{ width: '100%', padding: '12px 16px', borderRadius: 8, border: `1px solid ${C.border}`,
                                    background: C.white, color: C.text,
                                    fontSize: isMobile ? 16 : 14, minHeight: isMobile ? 48 : 'auto',
                                    boxSizing: 'border-box', fontFamily: 'inherit' }} />
                        </div>

                        <div style={{ marginBottom: 16 }}>
                            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: C.text, marginBottom: 6 }}>
                                {t('username')} <span style={{ color: C.red }}>*</span>
                            </label>
                            <input type="text" placeholder={t('enter_username')} value={username}
                                onChange={e => setUsername(e.target.value)} required autoComplete="username"
                                style={{ width: '100%', padding: '12px 16px', borderRadius: 8, border: `1px solid ${C.border}`,
                                    background: C.white, color: C.text,
                                    fontSize: isMobile ? 16 : 14, minHeight: isMobile ? 48 : 'auto',
                                    boxSizing: 'border-box', fontFamily: 'inherit' }} />
                        </div>

                        <div style={{ marginBottom: 24 }}>
                            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: C.text, marginBottom: 6 }}>
                                {t('password')} <span style={{ color: C.red }}>*</span>
                            </label>
                            <input type="password" placeholder={t('enter_password')} value={password}
                                onChange={e => setPassword(e.target.value)} required autoComplete="current-password"
                                style={{ width: '100%', padding: '12px 16px', borderRadius: 8, border: `1px solid ${C.border}`,
                                    background: C.white, color: C.text,
                                    fontSize: isMobile ? 16 : 14, minHeight: isMobile ? 48 : 'auto',
                                    boxSizing: 'border-box', fontFamily: 'inherit' }} />
                        </div>

                        {error && (
                            <div style={{ background: C.redLight, border: `1px solid ${C.redBorder}`, color: C.redText, borderRadius: 8, padding: '10px 14px', fontSize: 13, marginBottom: 16 }}>
                                {error}
                            </div>
                        )}

                        <button type="submit" disabled={loading} style={{
                            width: '100%', padding: '13px 0', borderRadius: 10, border: 'none',
                            cursor: loading ? 'not-allowed' : 'pointer', fontFamily: 'inherit',
                            fontSize: isMobile ? 16 : 15, fontWeight: 700, color: 'white',
                            background: C.green, minHeight: isMobile ? 48 : 'auto',
                            opacity: loading ? 0.7 : 1, transition: 'opacity 0.15s ease',
                        }}>
                            {loading ? t('signing_in') : isManager ? t('access_manager_portal') : t('continue_to_pos')}
                        </button>
                    </form>

                    {!isPosDevice && (
                        <div style={{ marginTop: 40, borderTop: `1px solid ${C.border}`, paddingTop: 20, textAlign: 'center' }}>
                            <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 1.5, color: C.mutedLight, textTransform: 'uppercase', marginBottom: 12 }}>{t('developers')}</div>
                            <a href="/system-access" style={{ fontSize: 12, color: C.mutedLight, textDecoration: 'none', fontWeight: 500 }}>
                                {t('system_access_super_admin')}
                            </a>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default LoginPage;
